import * as Sentry from '@sentry/browser';
import { TweenMax } from 'gsap';
import { getAssetList } from '../Config/Assets';
import { MapList, setBoardData } from '../Config/Levels';
import GameLoader from '../Core/Assets';
import Core from '../Core/Core';
import getLogger, { ILogger } from '../Core/Logger';
import { sizer } from '../index';
import { Actions } from '../State/Actions/Actions';
import { convertMapPlaceToScore, IStatePlayer } from '../State/reducers/player';
import { Views } from '../State/reducers/view';
import { IGameState } from '../State/Store/store';
import { getBoardJson } from '../Utils/Utils';
import GameView, { IView } from '../View/GameView';
import { Hud } from './Hud/Hud';
import Level from './Level/Level';
import { Manager } from './Manager';
import MapManager from './Map/Map';
import Messages from './Messages';
import Missions from './Missions';
import Tutorial from './Tutorial';

export interface IViewContainer {
    update: () => void;
    cleanUp: () => void;
    view: IView;
    init?: (data?: any[], loadstart?: number) => void | Promise<any>;
}

export default class Game extends Manager {
    private view: GameView;
    private hud: Hud;
    private container: IViewContainer;
    private bootload: boolean = true;
    private retrievingData: boolean = false;
    private messageMgr: Messages;
    private missionsMgr: Missions;
    private tutorial: Tutorial;
    private gameLogger: ILogger = getLogger('[GAME]:');
    private mapLeaderBoard: any[] = [];
    private loadStart: number;
    private mapLoaded: boolean;
    private gameLoaded: boolean;

    constructor(view: GameView) {
        super();
        this.view = view;
        this.messageMgr = new Messages();
        this.missionsMgr = new Missions();

        this.addSelector(
            (state: IStatePlayer, pre: IStatePlayer) => {
                const record = this.recordMapProgress(state, pre);
                record.catch(err => {
                    this.gameLogger.debug('record map progress err' + err);
                });
            },
            (state: IGameState) => state.StatePlayer
        );

        this.addSelector(
            (state: IStatePlayer, pre: IStatePlayer) => {
                const stars = this.recordStarProgress(state, pre);
                stars.catch(err => {
                    this.gameLogger.error('record stars add err' + err);
                });
            },
            (state: IGameState) => state.StatePlayer
        );

        this.addSelector(
            (state: IGameState, pre: IGameState) => {
                const res = this.respondToView(state, pre);
                res.catch(err => {
                    this.gameLogger.error('respond to view err: ' + err);
                });
            },
            (state: IGameState) => state
        );

        this.hud = new Hud(this.view);
        this.view.addHud(this.hud.view);
        this.tutorial = new Tutorial(this.view, this.hud);
        this.view.addTutorial(this.tutorial.view);

        this.gameLogger.debug('BOOT GAME');
        Core.store.dispatch(Actions.changeView(Views.BOOT));
    }

    public async update(): Promise<void> {
        if (this.container) {
            this.container.update();
        }

        if (this.hud) {
            this.hud.update();
        }

        if (this.view.isLoading) {
            this.view.update(Core.store.getState().StateView, this.retrievingData);
        }

        if (this.missionsMgr) {
            this.missionsMgr.update();
        }
    }

    public cleanUp() {
        this.messageMgr.cleanUp();
        this.missionsMgr.cleanUp();
        this.tutorial.cleanUp();
    }

    private async startFTUE(): Promise<void> {
        this.gameLogger.debug('START NEW FTUE');

        Core.store.dispatch(Actions.unlockMap(MapList[0]));
        await GameLoader.loadAssetList({
            assetList: [MapList[0] + '-levels'],
            onProgress: () => {},
            cb: () => {},
        }).catch(err => {
            Sentry.captureException(err);
            Sentry.captureMessage('Error with FTUE first load: ' + err);
            this.gameLogger.error('new user load map assets err: ' + err);
        });

        const res = getBoardJson(MapList[0] + '-1');
        setBoardData(res);

        Core.store.dispatch(Actions.initLevel(MapList[0] + '-1'));
        Core.store.dispatch(Actions.startLevel(MapList[0] + '-1')); // start level also switches view state to game

        this.gameLogger.debug('FTUE START COMPLETE');

        return;
    }

    private async respondToView(state: IGameState, pre: IGameState): Promise<void> {
        if (state.StateView !== pre.StateView) {
            this.tutorial.quitTutorial();
        }

        if (state.StateView === Views.MAP) {
            if (state.StatePlayer.mapId !== pre.StatePlayer.mapId) {
                await this.changeViewState(state.StateView, true).catch(err => {
                    Sentry.captureException(err);
                    Sentry.captureMessage('error switching maps: ' + err);
                    this.gameLogger.error('error with change view state with new map: ' + err);
                });
            }
        }

        if (state.StateView !== pre.StateView || this.bootload) {
            const newmap = state.StatePlayer.mapId !== pre.StatePlayer.mapId;
            await this.changeViewState(state.StateView, newmap).catch(err => {
                Sentry.captureException(err);
                Sentry.captureMessage('error switching views:' + err);
                this.gameLogger.error(
                    'error with change view state or boot: ' + err + ' loading ' + state.StateView
                );
            });
            if (newmap) {
                // this.initLoad = false;
            }
        }
    }

    private async recordMapProgress(state: IStatePlayer, pre: IStatePlayer): Promise<void> {
        const unlockedLevels = Object.entries(state.mapLevels);

        if (unlockedLevels.length > Object.entries(pre.mapLevels).length) {
            const newestUnlock = unlockedLevels[unlockedLevels.length - 1][0];
            const mapLevelScore = convertMapPlaceToScore(newestUnlock);
            Core.sdk.leaderboards
                .setLeaderBoardData('completedLevels', 'default', mapLevelScore)
                .catch(err => {
                    this.gameLogger.debug('set leaderboard progress error: ' + err);
                });
        }
    }

    private async recordStarProgress(state: IStatePlayer, pre: IStatePlayer): Promise<void> {
        if (state.totalStars !== pre.totalStars || this.bootload) {
            Core.sdk.leaderboards
                .setLeaderBoardData('leaderboard', 'default', state.totalStars)
                .catch(err => {
                    this.gameLogger.debug('set leaderboard score error: ' + err);
                });

            const starDiff = state.totalStars - pre.totalStars;
            if (starDiff > 0) {
                Core.sdk.leaderboards.setWeeklyLeaderBoardData(starDiff).catch(err => {
                    Sentry.captureException(err);
                    Sentry.captureMessage('error setting weekly score:' + err);
                    this.gameLogger.debug('set weekly score error: ' + err);
                });
            }
        }
    }

    private async changeViewState(newView: string, newMap?: boolean): Promise<void> {
        this.bootload = false;
        try {
            TweenMax.killAll(true, true, true, true);
        } catch (e) {
            this.gameLogger.warn('Could not kill tweens before changing view');
        }
        this.gameLogger.debug('change view to ' + newView);

        this.clearTutorial();
        this.clearContainer();

        const board = newView === Views.MAP ? 'completedLevels' : '';
        this.view.setLoading(true);
        this.loadStart = Date.now();

        // boot view is already loaded, and we do not want to trigger background loading after it
        if (newView !== Views.BOOT) {
            await this.prepareViewAssets(
                getAssetList(newView, Core.store.getState().StatePlayer, isNewPlayer(), true),
                newMap,
                board
            )
                .then(() => {
                    this.gameLogger.debug('change view load bundles done');
                })
                .catch(err => {
                    Sentry.captureException(err);
                    Sentry.captureMessage('error loading view bundles ' + err);
                    this.gameLogger.debug('error loading view assets: ' + err);
                });
        }

        await this.initView(newView);

        sizer();

        this.gameLogger.debug(' remove loading screen');
        if (newView !== Views.BOOT) {
            this.view.setLoading(false);
        }

        return;
    }

    private async initView(newView: string): Promise<void> {
        this.gameLogger.debug('init view: ' + newView);

        switch (newView) {
            case Views.MAP:
                await this.initMapView();
                break;
            case Views.GAME:
                await this.initGameView();
                break;
            case Views.EDITOR:
                break;
            case Views.BOOT:
                await this.initBoot();
                break;
        }

        if (this.container && this.container.view) {
            this.view.changeView(this.container.view);
        }
    }

    private async initBoot(): Promise<void> {
        Core.store.dispatch(Actions.initCore());
        if (NODE_ENV === 'editor') {
            await this.startEditorLevel(window.name).catch(err => {
                this.gameLogger.debug('error with editor mat loading: ' + err);
            });
        } else if (isNewPlayer()) {
            await this.startFTUE().catch(err => {
                this.gameLogger.debug('error with ftue start game: ' + err);
            });
        } else {
            this.setUpForReturning();
            this.gameLogger.debug('start game for returning player');
            this.view.setLoading(false);
            Core.store.dispatch(Actions.changeView(Views.MAP));
        }
    }

    private async initMapView(): Promise<void> {
        this.container = new MapManager(this.hud);
        const loadstart = !this.mapLoaded ? this.loadStart : null;
        this.mapLoaded = true;
        await (this.container.init(this.mapLeaderBoard, loadstart) as Promise<
            any
        >) /* .catch(err => {
            this.gameLogger.debug('error with map manager init: ' + err);
        }) */;
    }

    private async initGameView(): Promise<void> {
        const loadstart = !this.gameLoaded ? this.loadStart : null;
        this.gameLoaded = true;
        this.container = new Level(this.hud);
        await this.container.init([], loadstart);
        this.tutorial.setLevelReady(true);
        Core.store.dispatch(Actions.tutorialUpdate({ actionId: null, payload: null }));
    }

    private setUpForReturning(): void {
        // failsafe  for early users (Matti and Christian) that do not machu map unlocked because they started when rome was first
        // check if any machu levels played and unlock if not already
        const { StatePlayer } = Core.store.getState();
        const playedLevels = StatePlayer.mapLevels;

        if (!MapList.includes(StatePlayer.mapId)) {
            const allowed = StatePlayer.unlocked.filter(m => MapList.includes(m));
            Core.store.dispatch(Actions.changeMap(allowed[allowed.length - 1]));
        }
        const machuPlayed = Object.keys(playedLevels).filter(key => key.includes('machu'));
        if (
            machuPlayed.length > 0 &&
            !Core.store.getState().StatePlayer.unlocked.includes('machu')
        ) {
            Core.store.dispatch(Actions.unlockMap('machu'));
            this.gameLogger.debug('failsafe machu unlock');
            Sentry.captureMessage('failsafe machu unlock');
        }
    }

    private async backgroundLoading(newMap?: boolean) {
        const { StateView, StatePlayer } = Core.store.getState();
        const sounds: string[] = getSoundsForView(StateView, StatePlayer.mapId);
        sounds.push('common');
        await GameLoader.loadAssetList({
            assetList: sounds,
            newMap,
        });
        // prepare next view
        const nextView = StateView === Views.MAP ? Views.GAME : Views.MAP;
        await GameLoader.loadAssetList({
            assetList: getAssetList(nextView, StatePlayer, false, newMap),
            newMap,
        });

        const sfx: string[] = getSoundsForView(nextView, StatePlayer.mapId);
        await GameLoader.loadAssetList({
            assetList: sfx,
            newMap,
        });
    }

    private async prepareViewAssets(
        assetList: string[],
        newMap?: boolean,
        leaderboard?: string
    ): Promise<void> {
        this.gameLogger.debug(
            'loading assets for: ' + assetList + ', and leaderboard: ' + leaderboard
        );

        await GameLoader.loadAssetList({ assetList, newMap });
        this.backgroundLoading(newMap);

        if (leaderboard === 'completedLevels') {
            this.gameLogger.debug('loading completedLevels leaderboard');
            try {
                this.retrievingData = true;
                const l = await this.getLeaderBoard(leaderboard);
                this.setMapLeaderBoard(l);
            } catch (err) {
                this.gameLogger.debug(['error getting leaderboard during view loading', err]);
            } finally {
                this.retrievingData = false;
            }
        }
    }

    private getLeaderBoard(board: string): Promise<unknown> {
        this.gameLogger.debug('get leader board: ' + board);
        return Core.sdk.leaderboards.getLeaderBoardData(board, 'default');
    }

    private setMapLeaderBoard(leaderboard: any): void {
        this.mapLeaderBoard = leaderboard;
    }

    private clearTutorial(): void {
        this.tutorial.setLevelReady(false);
        this.tutorial.clearLevelStart();
    }

    private clearContainer(): void {
        if (this.container) {
            this.container.cleanUp();
            this.container = null;
        }
    }

    // editor stuff...
    private async startEditorLevel(state: string): Promise<void> {
        if (!state) {
            this.gameLogger.error('NO LEVELS TO LOAD');
            window.history.back();
        }
        this.gameLogger.debug('STARTING LEVEL FROM EDITOR ');

        try {
            Core.store.dispatch(
                Actions.updateCoins(69420 - Core.store.getState().StateCurrency.coin)
            );

            const levelData = JSON.parse(state).level.data;
            const levelNumber = Math.min(
                parseInt(levelData.config.levelId.split('-')[0], 10),
                MapList.length * 50
            );
            const mapIndex = Math.min(Math.floor((levelNumber - 1) / 50), MapList.length - 1);
            const mapName = MapList[mapIndex];
            await GameLoader.loadAssetList({
                assetList: [mapName + '-levels'],
                onProgress: () => {},
                cb: () => {
                    setBoardData(levelData);
                },
            }).catch(err => {
                this.gameLogger.debug('new user load map assets err: ' + err);
            });

            Core.store.dispatch(Actions.initLevel(mapName + '-' + levelNumber));
            Core.store.dispatch(Actions.startLevel(mapName + '-' + levelNumber));
        } catch (e) {
            this.gameLogger.debug('ERROR LOADING LEVEL ');
            window.history.back();
        }
    }
}

export function isNewPlayer(): boolean {
    const {
        StatePlayer: { mapLevels },
    } = Core.store.getState();
    const levelKeys = Object.keys(mapLevels);
    return (
        levelKeys.length === 0 || (levelKeys.length === 1 && mapLevels[levelKeys[0]].stars === 0)
    );
}

function getSoundsForView(view: string, map: string): string[] {
    const sfx: string[] = [map + '-' + view];
    if (view === Views.GAME) {
        sfx.push(view);
    }
    return sfx;
}
